import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RecebimentoService } from '../../../../../shared/services/recebimento.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { DateHelper } from '../../../../../shared/helpers/date.helper';
import { ToastState as ts } from '../../../../../shared/enums/toast-state.enum';
import { LcbFilaSeguranca } from '../../../models/lcb-fila-seguranca';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { InputComponent } from '../../../../../shared/components/input/input.component';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { TegelModule } from '@scania/tegel-angular-17';

import { CustomButtonComponent } from '../../../../../shared/components/custom-button/custom-button.component';
import { FaseId } from '../../../../../shared/enums/fase.enum';
import {
  SmsTemplateType,
  TipoLiberacao,
} from '../../../../../shared/enums/sms.enum';
import { NotificacaoService } from '../../../../../shared/services/notificacao.service';
import { SendSmsNotificationDTO } from '../../../models/sms-notification';
import { PhoneHelper } from '../../../../../shared/helpers/phone.helper';

@Component({
  selector: 'app-dialog-lcb-seguranca',
  imports: [
    ButtonComponent,
    InputComponent,
    ModalComponent,
    TegelModule,
    ReactiveFormsModule,
    CustomButtonComponent,
  ],
  templateUrl: './dialog-lcb-seguranca.component.html',
  styleUrl: './dialog-lcb-seguranca.component.scss',
})
export class DialogLcbSegurancaComponent implements OnInit {
  public fase = FaseId;
  public SmsTemplateType = SmsTemplateType;
  public TipoLiberacao = TipoLiberacao;

  form: FormGroup;
  filaId: number;
  recebimentoId: number;

  constructor(
    private dialogRef: MatDialogRef<DialogLcbSegurancaComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private fb: FormBuilder,
    private workflowService: RecebimentoService,
    private toast: ToastService,
    private notificacaoService: NotificacaoService,
  ) {
    this.filaId = data.id;
    this.recebimentoId = data.recebimentoId;

    const formattedDataEntrada = DateHelper.utcToLocalString(
      data.dataEntrada,
      'dd/MM/yyyy HH:mm:ss',
    );

    const driverInfo = data.driverInfo || {};

    const sessionPhone = sessionStorage.getItem(this.getPhoneKey());

    const phone =
      data?.recebimento?.celular || driverInfo.phone || sessionPhone || null;

    const fornecedorPreenchido =
      data?.recebimento?.fornecedor || driverInfo.fornMot || '';

    this.form = this.fb.group({
      senha: [data.recebimento.senha ?? ''],
      dataEntrada: formattedDataEntrada,
      registro: [data?.recebimento?.registro ?? ''],
      celular: [phone, [Validators.maxLength(19)]],
      critico: [data?.recebimento.critico ?? false],
      subidaDireta: [data?.recebimento.subidaDireta ?? false],
    });
  }

  ngOnInit(): void {
    const celularControl = this.form.get('celular');

    if (celularControl) {
      PhoneHelper.applyPhoneMaskOnInit(celularControl);

      celularControl.valueChanges.subscribe((value) => {
        if (value && value.length >= 10) {
          sessionStorage.setItem(this.getPhoneKey(), value);
        }
      });
    }
  }

  processar(proximaFaseId: number) {
    if (this.form.invalid) {
      this.toast.showToast(
        ts.error,
        'Revise os campos destacados antes de continuar.',
      );
      return;
    }

    const formValues = this.form.value;

    const dto: LcbFilaSeguranca = {
      filaId: this.filaId,
      recebimentoId: this.recebimentoId,
      proximaFaseId: proximaFaseId,
      registro: formValues.registro,
      celular: formValues.celular
        ? PhoneHelper.cleanForApi(formValues.celular)
        : null,
      critico: formValues.critico,
      subidaDireta: formValues.subidaDireta,
    };

    this.workflowService.processamentoLcbSeguranca(dto).subscribe({
      next: (result) => {
        this.toast.showToast(ts.success, 'Processado com sucesso!');

        sessionStorage.removeItem(this.getPhoneKey());

        this.dialogRef.close(result.content);
      },
      error: (e) => this.toast.showToast(ts.error, e.error[0]),
    });
  }

  enviarSms(
    templateType: SmsTemplateType,
    tipoLiberacao: TipoLiberacao | null = null,
  ) {
    const celularForm = this.form.get('celular')?.value;

    const celular = celularForm
      ? PhoneHelper.cleanForApi(celularForm)
      : this.data?.recebimento?.celular;

    const nomeMotorista = this.data?.motorista || 'Motorista';

    if (!celular) {
      this.toast.showToast(
        ts.warning,
        'Nenhum número de celular vinculado a este registro.',
      );
      return;
    }

    const dto: SendSmsNotificationDTO = {
      phoneNumber: celular,
      driverName: nomeMotorista,
      templateType: templateType,
      tipoLiberacao: tipoLiberacao,
    };

    this.notificacaoService.sendSms(dto).subscribe({
      next: (result) => {
        if (result.success) {
          this.toast.showToast(ts.success, 'SMS encaminhado para envio.');
        }
      },
      error: (e) => {
        const msg = e.error?.messages?.[0] || 'Falha ao enviar SMS.';
        this.toast.showToast(ts.error, msg);
      },
    });
  }

  onClose() {
    this.dialogRef.close();
  }

  private getPhoneKey(): string {
    return `lcb_phone_${this.recebimentoId}`;
  }
}
